"use client";

import { Fragment } from "react";
import { useTranslations } from "next-intl";
import { MessageCircleQuestion } from "lucide-react";
import { CHAIN_TEXT_SIZE } from "../../ai-elements/chain-of-thought";
import { StepDetail, type ToolPartBase } from "../ToolComponents";
import { settledQuestions } from "./questionTypes";

// A question part in the transcript. While it waits, the card under the chat
// asks it; once settled it reads as what was asked and what the user answered,
// the same whether the turn is live or reloaded.
export function QuestionToolRow({ part }: { part: ToolPartBase }) {
  const t = useTranslations("widget.questions");
  // The wire sends ``output-error``; the local union names it ``error``.
  const state: string = part.state;
  const waiting = state === "input-available";
  const settled = waiting
    ? null
    : settledQuestions(
        part.input,
        part.output,
        ["output-error", "error", "output-denied"].includes(state)
      );

  if (!waiting && !settled) return null;

  return (
    <div data-question-row className={CHAIN_TEXT_SIZE}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <MessageCircleQuestion
          className="size-3.5 shrink-0 opacity-70"
          aria-hidden
        />
        <span className="truncate">
          {waiting
            ? t("rowAsking")
            : settled?.kind === "answered"
              ? t("rowAnswered")
              : settled?.kind === "skipped"
                ? t("rowSkipped")
                : t("rowNotAnswered")}
        </span>
      </div>
      {settled?.kind === "answered" && (
        <StepDetail>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {settled.answers.map(({ question, answer }) => (
              <Fragment key={question.header}>
                <dt className="text-muted-foreground" title={question.question}>
                  {question.header}
                </dt>
                <dd className="min-w-0 font-medium text-foreground">
                  {answer.join(", ")}
                </dd>
              </Fragment>
            ))}
          </dl>
        </StepDetail>
      )}
      {settled && settled.kind !== "answered" && (
        <StepDetail>
          <p className="text-muted-foreground">
            {settled.questions.map((q) => q.header).join(", ")}
          </p>
        </StepDetail>
      )}
    </div>
  );
}
